
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";

import AppDialogSurface from "./AppDialogSurface";
import AppCard from "./AppCard";
import { APP_UI } from "../ui/appUi";
import type {
  MaterialMasterMismatchItem,
  MaterialMismatchField,
} from "../types/materialMasterData";

interface Props {
  open: boolean;
  materialNumber: string;
  mismatches: MaterialMasterMismatchItem[];
  masterDataCreatedAt: string | null;
  onClose: () => void;
  onApply: (fields: MaterialMismatchField[]) => void;
}

function formatMasterDataDate(value: string | null): string {
  if (!value) return "unbekannt";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin",
        dateStyle: "short",
        timeStyle: "short",
      }).format(date);
}

export default function MaterialMasterMismatchDialog({
  open,
  materialNumber,
  mismatches,
  masterDataCreatedAt,
  onClose,
  onApply,
}: Props) {
  const [selected, setSelected] = useState<Set<MaterialMismatchField>>(new Set());
  const fields = Array.from(selected);
  const allSelected = mismatches.length > 0 && fields.length === mismatches.length;

  const close = () => {
    setSelected(new Set());
    onClose();
  };

  const toggle = (field: MaterialMismatchField) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(mismatches.map((item) => item.field)));
  };

  const apply = () => {
    if (fields.length === 0) return;
    onApply(fields);
    setSelected(new Set());
  };

  return (
    <AppDialogSurface
      open={open}
      onClose={close}
      title="Materialstammliste abgleichen"
      subtitle={`Materialnummer ${materialNumber}`}
    >
      {mismatches.length > 0 ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", color: APP_UI.warning }}>
          <CompareArrowsRoundedIcon />
          <Typography variant="body2" sx={{ color: "#c7c8cc" }}>
            Etikett und Materialstammliste unterscheiden sich. Wähle die Werte aus, die übernommen werden sollen.
          </Typography>
        </Stack>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.25,
            py: 1.1,
            borderRadius: 2.4,
            border: "1px solid rgba(67,209,122,.32)",
            bgcolor: "rgba(67,209,122,.07)",
          }}
        >
          <CheckRoundedIcon sx={{ color: "#43d17a" }} />
          <Box>
            <Typography variant="body2" sx={{ color: "#7ee5a3", fontWeight: 900 }}>
              Materialstamm stimmt überein
            </Typography>
            <Typography variant="caption" sx={{ color: "#b7b9be" }}>
              Alle aktuellen Werte entsprechen der Materialstammliste.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Abschlussaktionen bewusst weit oben und nicht sticky. */}
      <Stack spacing={0.7}>
        <Button
          variant="contained"
          disabled={fields.length === 0}
          onClick={apply}
          sx={{
            minHeight: 50,
            borderRadius: 999,
            fontWeight: 850,
            textTransform: "none",
            "&.Mui-disabled": {
              bgcolor: "rgba(131,206,224,.10)",
              color: "rgba(225,239,243,.46)",
              border: "1px solid rgba(131,206,224,.16)",
            },
          }}
        >
          Übernehmen ({fields.length})
        </Button>
      </Stack>

      {mismatches.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 0.15 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DoneAllRoundedIcon />}
            disabled={allSelected}
            onClick={selectAll}
            sx={{
              width: "auto",
              minWidth: 0,
              px: 1.7,
              minHeight: 38,
              borderRadius: 999,
              textTransform: "none",
              color: allSelected ? "rgba(255,255,255,.30)" : APP_UI.accent,
              borderColor: allSelected
                ? "rgba(255,255,255,.10)"
                : APP_UI.accentBorder,
              fontWeight: 850,
            }}
          >
            Alle auswählen
          </Button>
        </Box>
      )}

      <Stack spacing={0.8}>
        {mismatches.map((item) => {
          const active = selected.has(item.field);
          return (
            <AppCard
              key={item.field}
              sx={{
                p: 0,
                overflow: "hidden",
                borderColor: active ? APP_UI.accentBorder : APP_UI.border,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  px: 1.25,
                  pt: 1,
                  color: APP_UI.textMuted,
                  fontWeight: 850,
                }}
              >
                {item.label}
              </Typography>

              <Box sx={{ px: 1.25, py: 0.75 }}>
                <Typography variant="caption" sx={{ color: "#7f8288" }}>
                  Etikett
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: APP_UI.text, fontWeight: 700 }}
                >
                  {item.labelValue || "–"}
                </Typography>
              </Box>

              <Button
                fullWidth
                onClick={() => toggle(item.field)}
                endIcon={active ? <CheckRoundedIcon /> : undefined}
                sx={{
                  justifyContent: "space-between",
                  textAlign: "left",
                  px: 1.25,
                  py: 1,
                  borderRadius: 0,
                  color: active ? "#b8edf7" : "#ffd39d",
                  bgcolor: active
                    ? APP_UI.accentSoft
                    : "rgba(244,163,64,.075)",
                  borderTop: `1px solid ${APP_UI.border}`,
                  fontWeight: 800,
                  textTransform: "none",
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: active ? APP_UI.accent : APP_UI.warning,
                      display: "block",
                    }}
                  >
                    Materialstammliste{" "}
                    {active ? "· ausgewählt" : "· zum Übernehmen tippen"}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {item.masterValue || "–"}
                  </Typography>
                </Box>
              </Button>
            </AppCard>
          );
        })}
      </Stack>

      <Typography
        variant="caption"
        sx={{ color: APP_UI.textMuted, textAlign: "center" }}
      >
        Stand der Materialstammliste: {formatMasterDataDate(masterDataCreatedAt)}
      </Typography>
    </AppDialogSurface>
  );
}
